import React, { useState, useEffect } from 'react';
import { formatCurrency, formatCurrencyInput } from '../utils/currency';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Sell,
  Person,
  AttachMoney,
  Receipt,
  Payment
} from '@mui/icons-material';

const SellBatch = ({ open, onClose, batch, onSubmit }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [saleData, setSaleData] = useState({
    // Customer Information
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    
    // Sale Details
    quantityToSell: batch?.current_count || batch?.count || 0,
    pricePerBird: '',
    totalAmount: '',
    saleDate: new Date().toISOString().split('T')[0],
    
    // Payment Information
    paymentMethod: 'Cash',
    paymentStatus: 'Pending',
    amountPaid: '',
    balanceAmount: '',
    
    // Additional Info
    notes: ''
  });
  const [error, setError] = useState('');

  const steps = ['Customer Info', 'Sale Details', 'Payment Info'];

  // Update quantityToSell when batch changes
  useEffect(() => {
    if (batch) {
      setSaleData(prev => ({
        ...prev,
        quantityToSell: batch.current_count || batch.count || 0
      }));
    }
  }, [batch]);

  if (!batch) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'pricePerBird') {
      const formattedValue = formatCurrencyInput(value);
      const numericValue = parseFloat(value.replace(/,/g, '')) || 0;
      const totalAmount = numericValue * saleData.quantityToSell;
      
      setSaleData(prev => ({
        ...prev,
        [name]: formattedValue,
        totalAmount: totalAmount.toString()
      }));
    } else if (name === 'quantityToSell') {
      const quantity = parseInt(value) || 0;
      const pricePerBird = parseFloat(saleData.pricePerBird.replace(/,/g, '')) || 0;
      const totalAmount = quantity * pricePerBird;
      
      setSaleData(prev => ({
        ...prev,
        [name]: value,
        totalAmount: totalAmount.toString()
      }));
    } else if (name === 'amountPaid') {
      const formattedValue = formatCurrencyInput(value);
      const paidAmount = parseFloat(value.replace(/,/g, '')) || 0;
      const totalAmount = parseFloat(saleData.totalAmount) || 0;
      const balance = totalAmount - paidAmount;
      
      setSaleData(prev => ({
        ...prev,
        [name]: formattedValue,
        balanceAmount: balance.toString()
      }));
    } else {
      setSaleData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Customer Info
        return saleData.customerName && saleData.customerPhone;
      case 1: // Sale Details
        return saleData.quantityToSell > 0 && saleData.pricePerBird && saleData.quantityToSell <= (batch.current_count || batch.count);
      case 2: // Payment Info
        return saleData.paymentMethod;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      setError('Please fill in all required fields');
      return;
    }
    
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = () => {
    if (!validateStep(2)) {
      setError('Please complete all required fields');
      return;
    }

    const sale = {
      id: Date.now(),
      batchId: batch.id,
      batchCode: batch.batch_code || batch.code,
      ...saleData,
      pricePerBird: parseFloat(saleData.pricePerBird.replace(/,/g, '')),
      totalAmount: parseFloat(saleData.totalAmount),
      amountPaid: parseFloat(saleData.amountPaid.replace(/,/g, '') || '0'),
      balanceAmount: parseFloat(saleData.balanceAmount || '0'),
      createdAt: new Date().toISOString(),
      invoiceNumber: `INV-${batch.batch_code || batch.code}-${Date.now().toString().slice(-6)}`
    };

    onSubmit(sale);
    handleClose();
  };

  const handleClose = () => {
    setSaleData({
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      customerAddress: '',
      quantityToSell: batch?.current_count || batch?.count || 0,
      pricePerBird: '',
      totalAmount: '',
      saleDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'Cash',
      paymentStatus: 'Pending',
      amountPaid: '',
      balanceAmount: '',
      notes: ''
    });
    setActiveStep(0);
    setError('');
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Customer Information
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person />
                Customer Information
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="customerName"
                label="Customer Name *"
                value={saleData.customerName}
                onChange={handleChange}
                fullWidth
                required
                placeholder="Full name of the customer"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="customerPhone"
                label="Phone Number *"
                value={saleData.customerPhone}
                onChange={handleChange}
                fullWidth
                required
                placeholder="+255 XXX XXX XXX"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="customerEmail"
                label="Email Address"
                type="email"
                value={saleData.customerEmail}
                onChange={handleChange}
                fullWidth
                placeholder="customer@example.com"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="saleDate"
                label="Sale Date"
                type="date"
                value={saleData.saleDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="customerAddress"
                label="Customer Address"
                value={saleData.customerAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Customer's address"
              />
            </Grid>
          </Grid>
        );

      case 1: // Sale Details
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt />
                Sale Details
              </Typography>
            </Grid>
            
            <Grid size={12}>
              <Card sx={{ backgroundColor: '#f5f5f5', mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Batch Information</Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="body2">
                      <strong>Batch Code:</strong> {batch.batch_code || batch.code}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Available Birds:</strong> {batch.current_count || batch.count}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {batch.type}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                name="quantityToSell"
                label="Quantity to Sell *"
                type="number"
                value={saleData.quantityToSell}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: 1, max: (batch.current_count || batch.count) }}
                helperText={`Max: ${batch.current_count || batch.count} birds`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                name="pricePerBird"
                label="Price per Bird (TZS) *"
                value={saleData.pricePerBird}
                onChange={handleChange}
                fullWidth
                required
                placeholder="0"
                helperText="Enter amount in TZS"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                name="totalAmount"
                label="Total Amount"
                value={formatCurrency(saleData.totalAmount)}
                fullWidth
                disabled
                helperText="Calculated automatically"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="notes"
                label="Sale Notes"
                value={saleData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes about this sale..."
              />
            </Grid>
          </Grid>
        );

      case 2: // Payment Information
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment />
                Payment Information
              </Typography>
            </Grid>

            <Grid size={12}>
              <Card sx={{ backgroundColor: '#e8f5e8', mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    Sale Summary
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Quantity:</Typography>
                    <Typography fontWeight="bold">{saleData.quantityToSell} birds</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Price per Bird:</Typography>
                    <Typography fontWeight="bold">{formatCurrency(saleData.pricePerBird.replace(/,/g, ''))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Total Amount:</Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatCurrency(saleData.totalAmount)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="paymentMethod"
                  value={saleData.paymentMethod}
                  onChange={handleChange}
                  label="Payment Method"
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Mobile Money">Mobile Money (M-Pesa/Tigo)</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Check">Check</MenuItem>
                  <MenuItem value="Credit">Credit (Pay Later)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  name="paymentStatus"
                  value={saleData.paymentStatus}
                  onChange={handleChange}
                  label="Payment Status"
                >
                  <MenuItem value="Paid">Fully Paid</MenuItem>
                  <MenuItem value="Partial">Partially Paid</MenuItem>
                  <MenuItem value="Pending">Pending Payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="amountPaid"
                label="Amount Paid (TZS)"
                value={saleData.amountPaid}
                onChange={handleChange}
                fullWidth
                placeholder="0"
                helperText="Amount received from customer"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="balanceAmount"
                label="Balance Amount"
                value={formatCurrency(saleData.balanceAmount)}
                fullWidth
                disabled
                helperText="Remaining amount to be paid"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sell color="primary" />
          <Typography variant="h6">Sell Batch - {batch?.batch_code || batch?.code}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} color="primary">
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext} 
            variant="contained" 
            color="primary"
            disabled={!validateStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="success"
            startIcon={<Sell />}
          >
            Create Sale
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SellBatch;
